/**
 * FoodPulse — Terms of Service (13 chapters).
 *
 * Structured as an array of chapters, each with a numbered list of
 * sections. Rendered by `/app/frontend/src/pages/FoodPulseTerms.jsx`
 * with sticky chapter navigation + `#chapter-N` bookmarked anchors so
 * users can deep-link to any chapter.
 */

export const FOODPULSE_TERMS_META = {
  title: "FoodPulse Terms of Service",
  effective: "August 1, 2026",
  updated: "August 15, 2026",
  intro:
    'FoodPulse(™) is a property of Social Pulse® Inc platform. FoodPulse is a global digital platform dedicated to connecting everyone in the food ecosystem while providing one of the world\'s most comprehensive collections of food knowledge. These Terms of Service ("Terms") constitute a legally binding agreement between FoodPulse ("FoodPulse, FoodPulse.net," "we," "our," or "us") and every person or entity that accesses or uses the FoodPulse website, mobile applications, APIs, software, communications, content, products, services, marketplaces, and related features (collectively, the "Platform"). By accessing or using any part of the Platform, you acknowledge that you have read, understood, and agree to be bound by these Terms.',
};

// Each chapter: {n, title, blurb?, sections: [{n, title, body?, bullets?, after_bullets?, bullets_2?, subgroups?, footer_note?}]}
export const FOODPULSE_TERMS = [
  {
    n: 1,
    title: "Introduction & Platform Basics",
    blurb: "Purpose, acceptance, definitions, eligibility, and account creation.",
    sections: [
      {
        n: 1, title: "Introduction",
        body: "FoodPulse is a global digital platform dedicated to connecting everyone in the food ecosystem while providing one of the world's most comprehensive collections of food knowledge. Our platform enables individuals and organizations to discover, learn about, buy, sell, share, donate, and discuss food products while promoting sustainability, reducing food waste, and supporting healthier communities.",
        after_bullets: "By accessing or using any part of the Platform, you acknowledge that you have read, understood, and agree to be bound by these Terms, together with our Privacy Policy and any additional policies or guidelines that apply to specific services offered through FoodPulse. If you do not agree to these Terms, you must not access or use the Platform.",
      },
      {
        n: 2, title: "About FoodPulse",
        body: "FoodPulse is designed to serve as both a comprehensive food information resource and a real-time digital marketplace that connects participants across the global food supply chain. Our services may include, but are not limited to:",
        bullets: [
          "Food knowledge and educational content", "Nutrition information",
          "Health and wellness resources", "Recipes and cooking guidance",
          "Food identification", "Agricultural information",
          "Local food discovery", "Real-time food availability",
          "Marketplace listings", "Food promotions and special offers",
          "Surplus food listings", "Food donation opportunities",
          "Community discussions", "Reviews and ratings",
          "Business directories", "Artificial intelligence powered recommendations",
          "Location-based services", "Images, videos, and multimedia",
          "Research resources", "Sustainability metrics",
          "Food waste reduction initiatives",
        ],
        footer_note: "FoodPulse continuously evolves, and we may add, modify, suspend, or discontinue services at any time.",
      },
      {
        n: 3, title: "Acceptance of These Terms",
        body: "By accessing or using FoodPulse, you represent and warrant that:",
        bullets: [
          "You have the legal capacity to enter into a binding agreement.",
          "You are at least the minimum age required by the laws of your jurisdiction to use the Platform.",
          "You will comply with these Terms and all applicable laws and regulations.",
          "All information you provide to FoodPulse is accurate and current.",
          "You will use the Platform only for lawful purposes.",
        ],
        footer_note: "If you are using FoodPulse on behalf of a company, organization, government agency, or other legal entity, you represent that you have the authority to bind that entity to these Terms.",
      },
      {
        n: 4, title: "Definitions",
        body: "For purposes of these Terms:",
        bullets: [
          "Account — a registered FoodPulse user account.",
          "Content — all text, photographs, graphics, recipes, videos, reviews, ratings, comments, data, software, code, and other materials available on or submitted through the Platform.",
          "User — any individual or organization accessing or using FoodPulse.",
          "Marketplace — any service that facilitates the discovery, promotion, sale, purchase, donation, or exchange of food products or related services.",
          "Seller — any individual or business offering products or services through FoodPulse.",
          "Buyer — any person purchasing or reserving products through the Platform.",
          "Business — farms, fisheries, food processors, wholesalers, distributors, retailers, restaurants, grocery stores, caterers, food banks, nonprofit organizations, and other commercial or nonprofit entities.",
          "User Content — any information, recipes, photographs, videos, reviews, comments, listings, or other material submitted by users.",
          "AI Content — summaries, recommendations, recipes, classifications, or other content generated or assisted by artificial intelligence technologies.",
        ],
      },
      {
        n: 5, title: "Eligibility",
        body: "Unless otherwise permitted by applicable law:",
        bullets: [
          "You must be at least 18 years old to create a FoodPulse account.",
          "Users under 18 may use certain informational features only with the involvement and consent of a parent or legal guardian.",
          "Businesses registering on behalf of an organization must have authority to act for that organization.",
        ],
        footer_note: "FoodPulse reserves the right to refuse registration, suspend access, or terminate accounts that do not meet eligibility requirements.",
      },
      {
        n: 6, title: "Creating an Account",
        body: "Certain features of the Platform require registration. When creating an account, you agree to:",
        bullets: [
          "Provide accurate, complete, and current information.",
          "Maintain and promptly update your information.",
          "Keep your login credentials secure and confidential.",
          "Notify FoodPulse immediately of any unauthorized use of your account.",
          "Accept responsibility for all activities that occur under your account unless caused by our negligence or security breach.",
        ],
        footer_note: "FoodPulse may require identity verification, business verification, email confirmation, phone verification, or additional documentation before granting access to certain services, including marketplace participation.",
      },
    ],
  },
  {
    n: 2,
    title: "User Accounts, Responsibilities & Platform Services",
    blurb: "Registration, security, acceptable use, and how the FoodPulse knowledge platform operates.",
    sections: [
      {
        n: 7, title: "User Accounts",
        body: "FoodPulse offers both public and registered services. While many informational features may be accessed without creating an account, certain services require registration, including participating in the marketplace, posting content, creating business profiles, saving favorites, receiving personalized recommendations, and accessing other interactive features. By creating a FoodPulse account, you agree to:",
        bullets: [
          "Provide truthful, accurate, and complete information during registration.",
          "Keep your account information current.",
          "Maintain only one personal account unless expressly authorized by FoodPulse.",
          "Maintain the confidentiality of your username, password, authentication credentials, and recovery information.",
          "Be responsible for all activities conducted through your account.",
          "Promptly notify FoodPulse if you suspect unauthorized access or misuse of your account.",
        ],
        footer_note: "FoodPulse reserves the right to request additional information to verify your identity or business affiliation before granting access to certain services.",
      },
      {
        n: 8, title: "Business Accounts",
        body: "Businesses may register specialized accounts, including but not limited to:",
        bullets: [
          "Farms", "Ranches", "Orchards", "Vineyards", "Fisheries",
          "Aquaculture operations", "Food manufacturers", "Wholesalers",
          "Distributors", "Grocery stores", "Restaurants", "Hotels",
          "Food trucks", "Caterers", "Farmers' markets", "Food banks",
          "Charitable organizations", "Government agencies",
          "Educational institutions", "Research organizations",
        ],
        footer_note: "Business accounts may be required to provide documentation verifying their identity, ownership, licensing, certifications, or regulatory compliance. FoodPulse reserves the right to approve, reject, suspend, or revoke business verification at its discretion. Verification by FoodPulse does not constitute an endorsement or guarantee of the business, its products, or its services.",
      },
      {
        n: 9, title: "Account Security",
        body: "You are responsible for maintaining the security of your account. You agree to:",
        bullets: [
          "Use strong passwords.",
          "Enable multi-factor authentication where available.",
          "Keep your contact information current.",
          "Log out from shared or public devices.",
          "Protect your account against unauthorized access.",
        ],
        after_bullets: "You may not:",
        bullets_2: [
          "Share your account credentials with another person.",
          "Sell or transfer your account.",
          "Allow another individual to impersonate you.",
          "Circumvent account security measures.",
          "Use automated tools to create accounts.",
        ],
        footer_note: "FoodPulse is not responsible for losses resulting from your failure to maintain account security.",
      },
      {
        n: 10, title: "User Responsibilities",
        body: "FoodPulse is built on trust, transparency, and respect. Every user is responsible for using the Platform in a lawful and respectful manner. Users agree to:",
        bullets: [
          "Comply with all applicable laws and regulations.",
          "Respect the rights of other users.",
          "Provide truthful information.",
          "Honor marketplace commitments.",
          "Respect intellectual property rights.",
          "Follow community guidelines.",
          "Report fraudulent or harmful activity.",
          "Cooperate with FoodPulse investigations when reasonably requested.",
        ],
      },
      {
        n: 11, title: "Acceptable Use",
        body: "FoodPulse may be used only for lawful purposes. You may use the Platform to:",
        bullets: [
          "Learn about food products.", "Discover local food.",
          "Buy and sell food where permitted.", "Share recipes.",
          "Upload photographs.", "Post reviews.",
          "Create educational content.", "Participate in community discussions.",
          "Promote sustainable food practices.", "Support food donation initiatives.",
          "Connect with businesses and consumers.",
        ],
        footer_note: "Any use outside these purposes that violates these Terms or applicable law is prohibited.",
      },
      {
        n: 12, title: "Prohibited Activities",
        body: "You may not use FoodPulse to:",
        bullets: [
          "Violate any law or regulation.", "Post false or misleading information.",
          "Commit fraud.", "Misrepresent products.", "Manipulate reviews or ratings.",
          "Upload malicious software.",
          "Harvest user information without authorization.",
          "Interfere with platform operations.", "Spam users.",
          "Send unsolicited commercial communications.",
          "Sell counterfeit products.", "Sell prohibited products.",
          "Upload viruses or malware.", "Attempt unauthorized access to systems.",
          "Reverse engineer FoodPulse software.",
          "Scrape platform data without written permission.",
          "Create fake accounts.",
          "Impersonate another person or organization.",
          "Infringe copyrights or trademarks.",
          "Publish defamatory or abusive content.",
          "Encourage illegal activities.", "Manipulate search results.",
          "Circumvent marketplace safeguards.",
        ],
        footer_note: "FoodPulse reserves the right to remove content and suspend accounts involved in prohibited conduct.",
      },
      {
        n: 13, title: "Platform Services",
        body: "FoodPulse provides a variety of services, including but not limited to:",
        subgroups: [
          {
            subtitle: "Knowledge Platform",
            body: "A comprehensive database covering:",
            bullets: [
              "Fruits", "Vegetables", "Herbs", "Grains", "Seafood",
              "Meat", "Dairy", "Spices", "Mushrooms", "Beverages",
              "Recipes", "Nutrition", "Sustainability", "Agricultural information",
            ],
          },
          {
            subtitle: "Marketplace",
            body: "Services enabling users to:",
            bullets: [
              "Discover products.", "Offer products.", "Find local deals.",
              "Reserve food.", "Promote surplus inventory.",
              "Connect with buyers.",
            ],
          },
          {
            subtitle: "Business Directory",
            body: "Profiles for:",
            bullets: [
              "Farms", "Restaurants", "Grocery stores", "Food producers",
              "Markets", "Food banks", "Agricultural organizations",
            ],
          },
          {
            subtitle: "Community Services",
            body: "Including:",
            bullets: [
              "Comments", "Reviews", "Ratings", "Questions and answers",
              "Discussions", "Educational resources", "Community contributions",
            ],
          },
          {
            subtitle: "AI Services",
            body: "Artificial intelligence may assist users by:",
            bullets: [
              "Recommending recipes.", "Summarizing nutrition.",
              "Suggesting substitutions.", "Identifying foods.",
              "Organizing information.", "Translating content.",
              "Improving search results.",
            ],
            footer_note: "AI-generated information should always be independently evaluated before making health, dietary, medical, agricultural, or commercial decisions.",
          },
        ],
      },
      {
        n: 14, title: "Food Knowledge Database",
        body: "FoodPulse strives to maintain one of the world's largest collections of food-related information. Information may include:",
        bullets: [
          "Scientific names", "Nutrition", "Health benefits", "Recipes",
          "Growing conditions", "Harvest seasons", "Storage methods",
          "Transportation", "Production statistics", "Sustainability metrics",
          "Cultural history", "Market trends",
        ],
        footer_note: "Although FoodPulse makes reasonable efforts to improve accuracy, we do not warrant that all information is complete, current, or error-free. Information is provided for educational and informational purposes only.",
      },
      {
        n: 15, title: "User Contributions",
        body: "FoodPulse encourages users to contribute knowledge that benefits the global community. Users may contribute:",
        bullets: [
          "Recipes", "Photos", "Videos", "Reviews", "Tips",
          "Growing advice", "Local names", "Historical information",
          "Food observations", "Market information", "Sustainability practices",
        ],
        after_bullets: "By submitting content, users represent that:",
        bullets_2: [
          "They own or have permission to share the content.",
          "The content is accurate to the best of their knowledge.",
          "The content does not violate applicable law or third-party rights.",
          "The content complies with FoodPulse policies.",
        ],
        footer_note: "User contributions help create a richer and more valuable knowledge platform but do not necessarily reflect the views or endorsements of FoodPulse.",
      },
      {
        n: 16, title: "Service Availability",
        body: "FoodPulse continually develops and improves its services. Accordingly, we may:",
        bullets: [
          "Add new features.", "Modify existing features.",
          "Suspend services for maintenance.", "Remove obsolete functionality.",
          "Introduce beta features.", "Change marketplace functionality.",
          "Improve AI capabilities.",
        ],
        footer_note: "We do not guarantee uninterrupted availability of the Platform or any specific feature and reserve the right to modify or discontinue services at our discretion.",
      },
      {
        n: 17, title: "Communications",
        body: "By creating an account or using FoodPulse, you agree that we may communicate with you regarding:",
        bullets: [
          "Account activity", "Security alerts", "Marketplace transactions",
          "Product updates", "Policy changes", "Service announcements",
          "Customer support",
          "Optional newsletters and promotional communications, where permitted by law and subject to your communication preferences",
        ],
        footer_note: "You may manage certain communication preferences through your account settings. Transactional and security-related communications may continue even if you opt out of promotional messages.",
      },
    ],
  },
  {
    n: 3,
    title: "Marketplace Services",
    blurb: "Buyer & seller rules, listings, pricing, food rescue, delivery, cancellations, refunds, and disputes.",
    sections: [
      {
        n: 18, title: "FoodPulse Marketplace",
        body: "The FoodPulse Marketplace is an online platform that enables eligible users and businesses to discover, promote, offer, reserve, buy, sell, donate, and exchange food products and related services. The Marketplace is designed to improve transparency across the food supply chain by connecting producers, suppliers, retailers, restaurants, institutions, food banks, charitable organizations, and consumers in real time.",
        footer_note: "FoodPulse provides the technology platform that facilitates these connections. Unless expressly stated otherwise, FoodPulse is not the seller, distributor, importer, exporter, broker, warehouse operator, transporter, or owner of products listed by third parties.",
      },
      {
        n: 19, title: "Marketplace Participants",
        body: "Marketplace participants may include:",
        bullets: [
          "Farmers", "Growers", "Orchards", "Vineyards", "Ranches",
          "Fisheries", "Aquaculture operations", "Food manufacturers",
          "Food processors", "Cooperatives", "Wholesalers", "Importers",
          "Exporters", "Distributors", "Grocery stores", "Supermarkets",
          "Convenience stores", "Restaurants", "Cafés", "Hotels", "Caterers",
          "Farmers markets", "Community Supported Agriculture (CSA) programs",
          "Food delivery providers", "Food banks", "Charitable organizations",
          "Educational institutions", "Government agencies", "Consumers",
        ],
      },
      {
        n: 20, title: "Nature of the Marketplace",
        body: "FoodPulse acts as a technology platform that enables users to discover and connect with one another. Except where expressly identified as the seller, FoodPulse does not:",
        bullets: [
          "Own listed inventory.", "Take title to products.",
          "Inspect every listed product.", "Guarantee availability.",
          "Guarantee pricing.", "Guarantee freshness.", "Guarantee quality.",
          "Guarantee authenticity.", "Guarantee delivery.",
          "Guarantee seller performance.",
        ],
        footer_note: "Contracts for the purchase, sale, reservation, donation, or exchange of products are entered into directly between participating users or businesses unless FoodPulse expressly provides otherwise.",
      },
      {
        n: 21, title: "Marketplace Listings",
        body: "Registered participants may create listings for eligible products and services. Listings may include:",
        bullets: [
          "Product name", "Scientific name", "Brand", "Variety", "Grade",
          "Quality classification", "Quantity available", "Unit of sale",
          "Packaging", "Harvest date", "Production date", "Best-before date",
          "Expiration date", "Country of origin", "Farm or producer",
          "Certifications", "Organic status", "Sustainability certifications",
          "Storage requirements", "Allergens", "Ingredients",
          "Nutritional information", "Photographs", "Videos", "Pricing",
          "Discounts", "Promotional offers", "Pickup location",
          "Delivery options", "Geographic availability",
        ],
        footer_note: "FoodPulse may require additional information for specific product categories.",
      },
      {
        n: 22, title: "Real-Time Availability",
        body: "FoodPulse supports real-time inventory updates whenever possible. However:",
        bullets: [
          "Inventory may change without notice.",
          "Listings may expire automatically.", "Products may sell out.",
          "Prices may change.", "Promotions may end.",
          "Quantities may be adjusted.",
          "Availability depends on the listing business.",
        ],
        footer_note: "FoodPulse does not guarantee that products displayed remain available at the time of purchase.",
      },
      {
        n: 23, title: "Pricing",
        body: "Sellers determine the prices of their products unless otherwise specified. Prices may include:",
        bullets: [
          "Regular retail prices", "Wholesale pricing", "Flash sales",
          "Daily specials", "Clearance pricing", "Surplus pricing",
          "Near-expiration discounts", "Bulk pricing", "Seasonal pricing",
          "Promotional pricing",
        ],
        footer_note: "Applicable taxes, delivery fees, service fees, packaging charges, deposits, and other costs may be displayed separately. FoodPulse reserves the right to display estimated prices but does not guarantee price accuracy.",
      },
      {
        n: 24, title: "Food Rescue & Surplus Marketplace",
        body: "One of FoodPulse's primary missions is reducing food waste. Businesses may list:",
        bullets: [
          "Surplus inventory", "Overstock products", "Imperfect produce",
          "Near-expiration products", "End-of-day bakery items",
          "Restaurant surplus meals", "Seasonal excess harvests",
          "Unsold farmers market inventory", "Donation-ready food",
        ],
        footer_note: "These listings help connect food with consumers and charitable organizations before it is discarded. Participation in Food Rescue programs remains voluntary unless otherwise required by applicable law.",
      },
      {
        n: 25, title: "Buyer Responsibilities",
        body: "Buyers agree to:",
        bullets: [
          "Review listings carefully.", "Read product descriptions.",
          "Verify allergens.", "Understand pickup or delivery requirements.",
          "Inspect products upon receipt when possible.",
          "Follow safe food handling practices.", "Pay agreed prices.",
          "Comply with seller policies.", "Report fraudulent listings.",
        ],
        footer_note: "Buyers are responsible for determining whether products meet their personal dietary, allergy, religious, medical, or nutritional requirements.",
      },
      {
        n: 26, title: "Seller Responsibilities",
        body: "Sellers agree to:",
        bullets: [
          "Provide truthful listings.", "Maintain accurate inventory.",
          "Honor accepted orders when reasonably possible.",
          "Promptly update unavailable products.", "Provide accurate pricing.",
          "Comply with food safety regulations.",
          "Maintain required licenses.", "Follow applicable labeling laws.",
          "Accurately disclose allergens.",
          "Accurately describe product condition.",
          "Respond promptly to customer inquiries.",
          "Cooperate in resolving disputes.",
        ],
        footer_note: "Sellers remain solely responsible for products they offer through FoodPulse.",
      },
      {
        n: 27, title: "Freshness & Product Condition",
        body: "Food products naturally vary in appearance, size, color, shape, ripeness, moisture, and shelf life. Reasonable variations do not necessarily constitute defects. Sellers should accurately describe product condition, including whether products are:",
        bullets: [
          "Fresh", "Frozen", "Refrigerated", "Dried", "Preserved",
          "Previously frozen", "Near expiration", "Cosmetic grade",
          "Processing grade",
        ],
      },
      {
        n: 28, title: "Pickup & Delivery",
        body: "Listings may offer:",
        bullets: [
          "Store pickup", "Farm pickup", "Local delivery",
          "Scheduled delivery", "Same-day delivery", "Courier delivery",
          "Third-party delivery", "Shipping where legally permitted",
        ],
        footer_note: "Delivery times are estimates unless otherwise stated. Risk of loss transfers according to the applicable agreement between buyer and seller and any governing law.",
      },
      {
        n: 29, title: "Cancellations",
        body: "Marketplace participants should honor confirmed transactions. However, orders may be cancelled due to:",
        bullets: [
          "Inventory shortages", "Product spoilage", "Severe weather",
          "Transportation disruptions", "Equipment failures",
          "Pricing errors", "Regulatory restrictions", "Food recalls",
          "Force majeure events",
        ],
        footer_note: "Cancellation policies may vary by seller. FoodPulse may establish standardized cancellation procedures for certain services.",
      },
      {
        n: 30, title: "Returns & Refunds",
        body: "Because many food products are perishable, return eligibility may be limited. Refunds may be available when:",
        bullets: [
          "Wrong products are delivered.", "Products are unsafe.",
          "Products are materially different from the listing.",
          "Products are damaged before delivery.",
          "Required items are missing.",
        ],
        footer_note: "Return and refund policies remain subject to applicable consumer protection laws and seller policies.",
      },
      {
        n: 31, title: "Food Recalls",
        body: "If a product becomes subject to a government or manufacturer recall:",
        bullets: [
          "Sellers must promptly remove affected listings.",
          "Sellers must notify affected buyers when required by law.",
          "FoodPulse may remove listings without notice.",
          "FoodPulse may display recall alerts.",
          "FoodPulse may suspend affected accounts pending investigation.",
        ],
        footer_note: "FoodPulse reserves the right to cooperate with regulatory authorities regarding food safety matters.",
      },
      {
        n: 32, title: "Marketplace Reviews & Ratings",
        body: "Verified buyers may submit product reviews, business reviews, seller ratings, delivery ratings, freshness ratings, and service feedback. Reviews must be honest, relevant, and based on actual experiences. FoodPulse may remove reviews that:",
        bullets: [
          "Are fraudulent.", "Contain hate speech.", "Include threats.",
          "Reveal private information.", "Promote illegal activity.",
          "Violate these Terms.",
        ],
      },
      {
        n: 33, title: "Marketplace Fees",
        body: "Certain marketplace services may require payment of:",
        bullets: [
          "Listing fees", "Subscription fees", "Advertising fees",
          "Transaction fees", "Payment processing fees",
          "Delivery service fees", "Premium service fees",
        ],
        footer_note: "Applicable fees will be disclosed before they are charged. FoodPulse may modify its fee schedule with reasonable notice where required by law.",
      },
      {
        n: 34, title: "Disputes Between Users",
        body: "FoodPulse encourages buyers and sellers to resolve disputes directly. Where available, FoodPulse may provide dispute resolution tools, customer support, or mediation services. Unless expressly agreed otherwise, FoodPulse is not obligated to resolve private disputes between marketplace participants.",
        footer_note: "FoodPulse reserves the right to investigate reports of fraud, abuse, or violations of these Terms and may suspend accounts or remove listings during an investigation.",
      },
      {
        n: 35, title: "Marketplace Integrity",
        body: "To maintain a trusted marketplace, FoodPulse may:",
        bullets: [
          "Verify businesses.", "Verify products.", "Monitor listings.",
          "Remove fraudulent content.", "Suspend suspicious accounts.",
          "Investigate complaints.", "Detect unusual activity.",
          "Prevent marketplace manipulation.", "Combat spam and scams.",
          "Cooperate with law enforcement and regulatory agencies when legally required.",
        ],
        footer_note: "Participants agree to cooperate with reasonable investigations relating to marketplace safety and integrity.",
      },
    ],
  },
  {
    n: 4,
    title: "Commercial Participants, Food Rescue & Sustainability",
    blurb: "Business categories, verification, farms, fisheries, retailers, donations, sustainability, traceability, and compliance.",
    sections: [
      {
        n: 36, title: "Commercial Participants",
        body: "FoodPulse welcomes businesses and organizations that contribute to the production, distribution, preparation, sale, donation, and sustainability of food. Commercial participants may include farms, orchards, vineyards, ranches, fisheries, aquaculture operations, greenhouses, food manufacturers, food processors, food distributors, importers, exporters, wholesalers, retail grocery stores, supermarkets, specialty food stores, farmers' markets, restaurants, cafés, bakeries, hotels, caterers, meal preparation services, delivery providers, food banks, charitable organizations, educational institutions, government agencies, research organizations, and agricultural cooperatives.",
        footer_note: "Commercial participants may access specialized services designed for their business category.",
      },
      {
        n: 37, title: "Business Verification",
        body: "FoodPulse may offer a Business Verification Program to improve trust and transparency. Verification may include review of:",
        bullets: [
          "Business registration", "Government-issued licenses",
          "Food handling permits", "Agricultural certifications",
          "Tax registration", "Organic certifications",
          "Sustainability certifications", "Food safety certifications",
          "Identity verification", "Business location verification",
          "Ownership verification",
        ],
        footer_note: "Verification status indicates only that certain information has been reviewed by FoodPulse. It does not constitute an endorsement, certification, warranty, guarantee, or assurance regarding a participant's products, services, financial condition, or regulatory compliance. FoodPulse may revoke verification at any time if information becomes inaccurate or misleading.",
      },
      {
        n: 38, title: "Farmers, Growers and Agricultural Producers",
        body: "FoodPulse supports local, regional, national, and international agricultural producers. Participating producers may:",
        bullets: [
          "Create farm profiles", "List seasonal harvests",
          "Promote fresh produce", "Publish crop availability",
          "Share growing practices", "Display certifications",
          "Participate in local marketplaces",
          "Offer direct-to-consumer sales where permitted",
          "Accept reservations", "Promote farm events",
          "Share educational information",
        ],
        footer_note: "Agricultural producers remain solely responsible for compliance with all applicable agricultural, food safety, environmental, labor, labeling, and licensing laws.",
      },
      {
        n: 39, title: "Fisheries and Aquaculture",
        body: "Fisheries and aquaculture operations may list seafood products through FoodPulse where permitted by law. Participants should accurately disclose information including, where applicable:",
        bullets: [
          "Species", "Harvest method", "Catch area", "Farming method",
          "Sustainability certifications", "Processing method",
          "Fresh or frozen status", "Harvest date", "Packaging information",
        ],
        footer_note: "FoodPulse encourages responsible fishing and sustainable aquaculture practices but does not independently verify all claims unless expressly stated.",
      },
      {
        n: 40, title: "Grocery Stores and Food Retailers",
        body: "Retail food businesses may use FoodPulse to:",
        bullets: [
          "Publish inventory", "Advertise promotions", "Offer digital coupons",
          "Promote clearance items", "Sell surplus inventory",
          "List seasonal products", "Offer online reservations",
          "Provide pickup services", "Coordinate local delivery",
          "Share product information",
        ],
        footer_note: "Retailers remain responsible for maintaining accurate pricing, inventory, and compliance with consumer protection and food safety laws.",
      },
      {
        n: 41, title: "Restaurants, Cafés and Food Service Providers",
        body: "Restaurants and food service businesses may use FoodPulse to:",
        bullets: [
          "Publish menus", "Promote daily specials", "Offer prepared meals",
          "Sell surplus meals", "Advertise limited-time offers",
          "Accept reservations where available", "Promote catering services",
          "Share recipes and culinary expertise",
          "Participate in food rescue initiatives",
        ],
        footer_note: "Restaurants are solely responsible for food preparation, sanitation, ingredient disclosure, allergen information, and compliance with applicable health regulations.",
      },
      {
        n: 42, title: "Food Manufacturers and Processors",
        body: "Food manufacturers and processors may use FoodPulse to:",
        bullets: [
          "Showcase products", "Publish ingredient information",
          "Share nutrition data", "Promote new product launches",
          "Communicate product recalls", "Connect with distributors",
          "Reach consumers", "Support educational initiatives",
        ],
        footer_note: "Manufacturers remain responsible for ensuring the accuracy of product information, regulatory compliance, and product safety.",
      },
      {
        n: 43, title: "Food Donations and Food Rescue",
        body: "Reducing food waste is a core mission of FoodPulse. The Platform may facilitate connections between businesses, charitable organizations, food banks, and community groups for the donation or redistribution of surplus food. Examples include:",
        bullets: [
          "Excess farm harvests", "Unsold grocery inventory",
          "Surplus prepared meals", "Bakery products",
          "Produce nearing peak ripeness", "Food from community events",
          "Shelf-stable products",
          "Frozen products suitable for donation",
        ],
        footer_note: "Participation in donation programs is voluntary unless otherwise required by applicable law.",
      },
      {
        n: 44, title: "Responsibilities of Donors",
        body: "Businesses or individuals donating food through FoodPulse agree to:",
        bullets: [
          "Comply with applicable food donation laws.",
          "Donate only food reasonably believed to be safe for consumption.",
          "Accurately describe donated products.",
          "Disclose known allergens when applicable.",
          "Follow appropriate storage and transportation practices.",
          "Cooperate with recipient organizations.",
        ],
        footer_note: "FoodPulse encourages donations made in good faith but does not inspect donated products.",
      },
      {
        n: 45, title: "Responsibilities of Recipient Organizations",
        body: "Food banks, charities, and other recipient organizations are responsible for:",
        bullets: [
          "Determining whether donated food is suitable for distribution.",
          "Maintaining appropriate storage conditions.",
          "Following applicable health regulations.",
          "Inspecting donated products before acceptance.",
          "Complying with local food safety requirements.",
        ],
        footer_note: "Recipient organizations accept donated products at their own discretion.",
      },
      {
        n: 46, title: "Sustainability Initiatives",
        body: "FoodPulse promotes environmentally responsible practices throughout the food ecosystem. The Platform may support initiatives related to:",
        bullets: [
          "Food waste reduction", "Sustainable agriculture",
          "Regenerative farming", "Organic farming",
          "Water conservation", "Soil health", "Biodiversity",
          "Pollinator protection", "Responsible fishing",
          "Carbon footprint reduction", "Sustainable packaging",
          "Local food systems", "Circular economy practices",
        ],
      },
      {
        n: 47, title: "Product Traceability",
        body: "FoodPulse may provide optional tools that allow businesses to share information regarding the origin and movement of food products. Traceability information may include:",
        bullets: [
          "Farm or producer", "Harvest location", "Processing facility",
          "Distribution chain", "Batch or lot numbers", "Production dates",
          "Certifications", "Transportation methods",
        ],
        footer_note: "FoodPulse does not independently verify all traceability information and relies on information provided by participating businesses.",
      },
      {
        n: 48, title: "Certifications and Claims",
        body: "Businesses may display certifications or product claims only if they are truthful, current, and supported by appropriate documentation where required. Examples include:",
        bullets: [
          "Organic", "Non-GMO", "Fair Trade", "Rainforest Alliance",
          "Certified Humane", "MSC", "ASC", "Kosher", "Halal",
          "Gluten-Free", "Vegan", "Vegetarian",
          "Regenerative Agriculture", "Sustainable Seafood",
        ],
        footer_note: "FoodPulse reserves the right to request supporting documentation and remove unsupported or misleading claims.",
      },
      {
        n: 49, title: "Regulatory Compliance",
        body: "Commercial participants are solely responsible for complying with all applicable laws and regulations, including those related to:",
        bullets: [
          "Food safety", "Labeling", "Advertising", "Consumer protection",
          "Weights and measures", "Licensing", "Taxation", "Employment",
          "Environmental protection", "Animal welfare",
          "Import and export controls", "Data protection",
        ],
      },
      {
        n: 50, title: "Commercial Promotions",
        body: "Businesses may promote products and services through FoodPulse using advertising, featured listings, sponsored content, coupons, loyalty programs, or other promotional tools offered by the Platform. Promotional content must:",
        bullets: [
          "Be truthful and not misleading.",
          "Clearly disclose material terms and conditions.",
          "Comply with applicable advertising laws.",
          "Respect intellectual property rights.",
          "Avoid deceptive or unfair business practices.",
        ],
        footer_note: "FoodPulse reserves the right to review, reject, suspend, or remove promotional content that violates these Terms or applicable law.",
      },
      {
        n: 51, title: "Commercial Responsibility",
        body: "Each commercial participant is independently responsible for:",
        bullets: [
          "The products and services it offers.", "Customer communications.",
          "Pricing decisions.", "Inventory management.", "Product quality.",
          "Food safety.", "Packaging.", "Delivery arrangements.",
          "Customer support.", "Regulatory compliance.",
        ],
        footer_note: "FoodPulse provides the technology platform but does not assume responsibility for the day-to-day operations or legal obligations of participating businesses.",
      },
    ],
  },
  {
    n: 5,
    title: "User Content, Community Contributions & Intellectual Property License",
    blurb: "Ownership of contributions, the license granted to FoodPulse, community standards, and content moderation.",
    sections: [
      {
        n: 52, title: "Community Contributions",
        body: "FoodPulse is built on the knowledge and experiences of its global community. Users may contribute content including, but not limited to recipes, cooking instructions, food preparation techniques, product reviews, restaurant reviews, business reviews, ratings, comments, questions and answers, articles, blog posts, educational content, farming practices, gardening tips, harvest information, seasonal observations, food photographs, videos, illustrations, audio recordings, infographics, food identification, local names, scientific information, historical information, cultural traditions, sustainability practices, market observations, and event information.",
        footer_note: 'Collectively, these materials are referred to as "User Content."',
      },
      {
        n: 53, title: "Ownership of User Content",
        body: "You retain ownership of the intellectual property rights in the original content you create and submit to FoodPulse. Submitting User Content does not transfer ownership of your copyright to FoodPulse. However, by submitting User Content, you grant FoodPulse the license described in these Terms to operate, improve, and promote the Platform.",
      },
      {
        n: 54, title: "License Granted to FoodPulse",
        body: "By submitting User Content, you grant FoodPulse a worldwide, non-exclusive, royalty-free, transferable, sublicensable license to:",
        bullets: [
          "Host", "Store", "Display", "Publish", "Reproduce", "Copy",
          "Translate", "Format", "Adapt", "Resize", "Index", "Archive",
          "Distribute", "Publicly perform", "Publicly display",
          "Promote", "Analyze", "Improve search functionality",
          "Train recommendation systems",
          "Create previews and excerpts",
          "Feature your content within FoodPulse services",
        ],
        footer_note: "This license applies solely for the purpose of operating, improving, promoting, securing, and expanding FoodPulse and its related services. Except where you separately agree, FoodPulse will not sell your original copyrighted works as standalone commercial products.",
      },
      {
        n: 55, title: "User Representations",
        body: "When submitting User Content, you represent and warrant that:",
        bullets: [
          "You own the content or have the legal right to submit it.",
          "Your submission does not infringe the rights of others.",
          "The information is accurate to the best of your knowledge.",
          "The content complies with applicable laws.",
          "The content complies with these Terms and Community Guidelines.",
          "You have obtained necessary permissions from identifiable individuals appearing in your submitted media where required by law.",
        ],
        footer_note: "You remain solely responsible for your User Content.",
      },
      {
        n: 56, title: "Community Standards",
        body: "Users agree to contribute respectfully and responsibly. User Content must not:",
        bullets: [
          "Be fraudulent", "Be misleading", "Encourage illegal activity",
          "Promote unsafe food practices", "Contain hate speech",
          "Contain threats", "Harass individuals", "Defame others",
          "Include pornography", "Promote violence", "Promote terrorism",
          "Contain malicious software", "Violate privacy rights",
          "Reveal confidential information",
          "Encourage eating disorders or dangerous health practices",
          "Promote fraudulent health claims",
          "Violate intellectual property rights",
        ],
        footer_note: "FoodPulse reserves the right to remove content that violates these standards.",
      },
      {
        n: 57, title: "Recipes and Culinary Content",
        body: "Recipes are one of the core features of FoodPulse. Users may contribute family recipes, traditional recipes, cultural recipes, restaurant-inspired recipes, healthy recipes, vegetarian recipes, vegan recipes, gluten-free recipes, seasonal recipes, holiday recipes, children's recipes, and chef recipes. Recipe submissions may include:",
        bullets: [
          "Ingredients", "Instructions", "Cooking times",
          "Nutritional estimates", "Preparation tips", "Serving suggestions",
          "Photos", "Videos",
        ],
        footer_note: "Users are responsible for ensuring recipe accuracy. FoodPulse does not guarantee cooking results or nutritional outcomes.",
      },
      {
        n: 58, title: "Reviews and Ratings",
        body: "FoodPulse allows users to submit reviews and ratings regarding food products, businesses, restaurants, farms, markets, recipes, and educational content. Reviews should:",
        bullets: [
          "Reflect genuine experiences.", "Be honest and respectful.",
          "Avoid personal attacks.", "Avoid false statements.",
          "Avoid conflicts of interest.",
        ],
        footer_note: "FoodPulse may remove reviews that violate these Terms or applicable law.",
      },
      {
        n: 59, title: "Photographs, Videos and Media",
        body: "Users may upload original media that enhances the FoodPulse community. Examples include fruits, vegetables, herbs, farms, markets, wildlife affecting crops, gardens, kitchens, recipes, finished meals, restaurants, agricultural practices, harvests, food festivals, and educational demonstrations.",
        footer_note: "Uploaded media should accurately represent the subject depicted and must not infringe the rights of others.",
      },
      {
        n: 60, title: "Educational Content",
        body: "FoodPulse encourages educational contributions from researchers, universities, agricultural experts, nutritionists, dietitians, chefs, gardeners, farmers, food scientists, and historians.",
        footer_note: "Educational materials should identify sources where appropriate and distinguish clearly between factual information, professional opinions, and personal experiences.",
      },
      {
        n: 61, title: "Attribution",
        body: "Where practical, FoodPulse may display the name, username, business name, organization, or other attribution associated with User Content. Users may choose display names where supported by the Platform. FoodPulse reserves the right to reformat attribution for consistency and usability.",
      },
      {
        n: 62, title: "AI-Assisted Content",
        body: "FoodPulse may use artificial intelligence to assist users by improving readability, translating content, organizing information, generating summaries, suggesting tags, recommending categories, detecting duplicate content, and improving search results.",
        footer_note: "AI assistance does not change ownership of the original User Content. FoodPulse may identify AI-assisted or AI-generated content where appropriate.",
      },
      {
        n: 63, title: "Content Moderation",
        body: "To protect users and the integrity of the Platform, FoodPulse may:",
        bullets: [
          "Review submitted content.", "Moderate discussions.",
          "Remove inappropriate content.", "Restrict visibility.",
          "Add informational notices.",
          "Request clarification or corrections.",
          "Suspend or terminate repeat violators.",
          "Preserve content where required by law.",
        ],
        footer_note: "FoodPulse may use a combination of human review and automated systems to support moderation.",
      },
      {
        n: 64, title: "Reporting Content",
        body: "Users may report content believed to:",
        bullets: [
          "Violate these Terms.", "Infringe copyrights.",
          "Infringe trademarks.", "Contain false information.",
          "Promote scams.", "Endanger public health.",
          "Encourage unsafe food practices.", "Contain illegal material.",
        ],
        footer_note: "FoodPulse will review reports in accordance with its moderation procedures and applicable law.",
      },
      {
        n: 65, title: "Removal of User Content",
        body: "FoodPulse may remove, restrict, edit, or disable access to User Content that:",
        bullets: [
          "Violates these Terms.", "Violates Community Guidelines.",
          "Creates legal risk.", "Is fraudulent.",
          "Is materially inaccurate.", "Is harmful to users.",
          "Infringes intellectual property rights.",
          "Is required to be removed by law or court order.",
        ],
        footer_note: "Removal of content does not necessarily affect ownership of the underlying intellectual property.",
      },
      {
        n: 66, title: "Preservation of Content",
        body: "FoodPulse may archive, back up, or preserve User Content for operational, legal, security, research, or historical purposes, subject to applicable law and our Privacy Policy.",
        footer_note: "Users are responsible for maintaining their own copies of important content. FoodPulse does not guarantee permanent storage or availability of any User Content.",
      },
      {
        n: 67, title: "Feedback and Suggestions",
        body: "If you voluntarily submit suggestions, feature requests, comments, or other feedback, you grant FoodPulse a perpetual, worldwide, royalty-free license to use, modify, implement, and incorporate that feedback without compensation or obligation to you, unless otherwise agreed in writing.",
      },
      {
        n: 68, title: "Respect for Intellectual Property",
        body: "FoodPulse respects the intellectual property rights of creators and expects users to do the same. Users may not upload, reproduce, distribute, or display content that infringes copyrights, trademarks, patents, trade secrets, or other proprietary rights.",
        footer_note: "FoodPulse will respond to valid intellectual property complaints in accordance with applicable law and our Copyright and DMCA Policy.",
      },
    ],
  },
  {
    n: 6,
    title: "AI, Nutrition, Health Information & Food Safety",
    blurb: "Educational purpose, AI disclaimers, nutrition, allergies, recipes, food identification, and safety.",
    sections: [
      {
        n: 69, title: "Educational Purpose",
        body: "FoodPulse is an educational, informational, and community platform. Unless expressly stated otherwise, all content available through the Platform is provided solely for informational and educational purposes.",
        footer_note: "Nothing on FoodPulse should be interpreted as professional medical, nutritional, dietary, agricultural, legal, financial, or scientific advice.",
      },
      {
        n: 70, title: "Artificial Intelligence Services",
        body: 'FoodPulse may use artificial intelligence ("AI") to improve user experience and assist with content creation, organization, search, recommendations, translations, summaries, and other services. AI features may include:',
        bullets: [
          "Food identification", "Ingredient recognition",
          "Recipe generation", "Recipe recommendations",
          "Meal planning suggestions", "Nutrition summaries",
          "Health benefit summaries", "Food substitutions",
          "Seasonal recommendations", "Product categorization",
          "Language translation", "Search assistance",
          "Personalized recommendations", "Image analysis",
          "Knowledge summaries",
        ],
        footer_note: "AI technologies continue to evolve and may occasionally produce inaccurate, incomplete, outdated, biased, or misleading information. Users should independently verify important information before relying upon it.",
      },
      {
        n: 71, title: "AI-Generated Content Disclaimer",
        body: "AI-generated or AI-assisted content is provided for convenience and educational purposes only. FoodPulse does not guarantee that AI-generated content is:",
        bullets: [
          "Accurate", "Complete", "Current", "Scientifically validated",
          "Appropriate for every individual", "Free from errors",
        ],
        after_bullets: "Users remain responsible for evaluating AI-generated information before making decisions relating to:",
        bullets_2: [
          "Health", "Nutrition", "Allergies", "Medical treatment",
          "Food preparation", "Food safety", "Commercial activities",
          "Agricultural decisions",
        ],
      },
      {
        n: 72, title: "Nutrition Information",
        body: "Nutrition information may include calories, macronutrients, vitamins, minerals, fiber, sugar, sodium, fat, protein, cholesterol, water content, glycemic index, micronutrients, and antioxidants. Nutritional values are estimates unless expressly stated otherwise and may vary based on:",
        bullets: [
          "Variety", "Growing conditions", "Harvest time", "Storage",
          "Preparation", "Cooking method", "Processing", "Portion size",
        ],
        footer_note: "FoodPulse does not guarantee nutritional accuracy for every product.",
      },
      {
        n: 73, title: "Health Information Disclaimer",
        body: "FoodPulse may discuss potential health benefits associated with foods, ingredients, nutrients, or dietary patterns. Such information is intended solely for general education. FoodPulse does not:",
        bullets: [
          "Practice medicine.", "Diagnose diseases.", "Prescribe treatments.",
          "Recommend medications.", "Replace licensed healthcare providers.",
          "Establish individualized nutrition plans.",
        ],
        footer_note: "Always seek advice from qualified healthcare professionals regarding medical conditions, medications, allergies, pregnancy, dietary restrictions, or health concerns.",
      },
      {
        n: 74, title: "Dietary Information",
        body: "FoodPulse may provide information relating to various dietary lifestyles and preferences, including but not limited to:",
        bullets: [
          "Vegetarian", "Vegan", "Gluten-Free", "Dairy-Free", "Kosher",
          "Halal", "Paleo", "Keto", "Mediterranean",
          "Low-Carbohydrate", "Low-Sodium", "High-Protein", "Plant-Based",
        ],
        footer_note: "These classifications are provided for convenience. Users are responsible for determining whether products meet their personal dietary, religious, ethical, or medical requirements.",
      },
      {
        n: 75, title: "Allergy Disclaimer",
        body: "Food allergies and sensitivities can be serious. Although FoodPulse encourages businesses and contributors to disclose ingredients and allergens accurately, FoodPulse does not independently verify allergen information for every product, recipe, or listing. Users should:",
        bullets: [
          "Read product labels.", "Confirm ingredients with sellers.",
          "Contact restaurants directly.",
          "Verify allergen information before consuming food.",
          "Exercise caution if they have allergies or food sensitivities.",
        ],
        footer_note: "FoodPulse is not responsible for allergic reactions resulting from reliance on information available through the Platform.",
      },
      {
        n: 76, title: "Recipe Disclaimer",
        body: "Recipes published on FoodPulse are intended for educational and culinary inspiration. Cooking results may vary depending upon:",
        bullets: [
          "Ingredients", "Equipment", "Skill level", "Cooking techniques",
          "Altitude", "Climate", "Measurements", "Ingredient substitutions",
        ],
        footer_note: "Users remain responsible for safely preparing food and ensuring recipes are appropriate for their individual dietary needs.",
      },
      {
        n: 77, title: "Food Identification",
        body: "FoodPulse may provide tools that identify fruits, vegetables, herbs, mushrooms, fish, seafood, plants, grains, seeds, spices, and prepared foods using artificial intelligence, community contributions, image recognition, or machine learning.",
        footer_note: "Identification results should be independently verified before consuming wild plants, mushrooms, seafood, or other foods. Never consume unfamiliar foods solely based upon AI identification.",
      },
      {
        n: 78, title: "Food Safety",
        body: "Food safety remains the responsibility of producers, sellers, restaurants, processors, distributors, and consumers. FoodPulse encourages users to follow recognized food safety practices including:",
        bullets: [
          "Proper refrigeration", "Safe cooking temperatures",
          "Cross-contamination prevention", "Hand hygiene",
          "Safe food storage", "Expiration monitoring",
          "Proper food handling",
        ],
        footer_note: "FoodPulse does not inspect food products listed by third parties unless expressly stated.",
      },
      {
        n: 79, title: "Product Information",
        body: "Product descriptions available on FoodPulse may be provided by businesses, manufacturers, farmers, government agencies, community members, or artificial intelligence. FoodPulse cannot guarantee that every product description is complete, current, or error-free.",
      },
      {
        n: 80, title: "Research and Scientific Information",
        body: "FoodPulse may summarize scientific publications, agricultural research, nutrition studies, and educational materials. Scientific understanding evolves over time. Research findings should not be interpreted as universally accepted facts or individualized recommendations.",
      },
      {
        n: 81, title: "External Resources",
        body: "FoodPulse may reference or link to government agencies, universities, scientific journals, research organizations, businesses, educational websites, videos, and news articles. These resources are provided solely for convenience. FoodPulse does not endorse every statement made by third-party sources and is not responsible for their content or availability.",
      },
      {
        n: 82, title: "Emergency Situations",
        body: "FoodPulse is not an emergency service. Do not rely upon FoodPulse during medical, food poisoning, allergic, or other emergencies. If you believe you are experiencing a medical emergency or severe allergic reaction, seek immediate assistance from qualified emergency medical services.",
      },
      {
        n: 83, title: "Limitation of Reliance",
        body: "You acknowledge and agree that any reliance upon information available through FoodPulse is at your own risk. FoodPulse encourages users to consult qualified professionals whenever important health, nutrition, agricultural, commercial, or legal decisions are involved.",
      },
      {
        n: 84, title: "Continuous Improvement",
        body: "FoodPulse continually reviews, updates, and improves its educational content, AI systems, nutritional information, recipes, and food knowledge database. Because food science, nutrition research, regulations, agricultural practices, and technology evolve over time, information available through the Platform may change without notice.",
        footer_note: "Users are encouraged to consult the most recent information available and report potential inaccuracies through FoodPulse's feedback and reporting tools.",
      },
    ],
  },
  {
    n: 7,
    title: "Intellectual Property, Copyright, Trademarks & Licensing",
    blurb: "Ownership of the platform, trademarks, user IP, DMCA, API access, and licensing.",
    sections: [
      {
        n: 85, title: "Ownership of the Platform",
        body: "FoodPulse and its licensors own all rights, title, and interest in and to the Platform, including its software, technology, architecture, databases, user interface, design, branding, and proprietary content, except for User Content owned by users or third-party content owned by its respective rights holders. This includes, but is not limited to:",
        bullets: [
          "Website and mobile application designs",
          "Source code and object code", "Databases and data structures",
          "Search technology",
          "Artificial intelligence models and workflows developed by or for FoodPulse",
          "Algorithms", "User interface elements", "Graphics and icons",
          "Logos", "Branding", "Documentation", "APIs", "Platform features",
          "Compilations and original editorial content",
        ],
        footer_note: "Nothing in these Terms transfers ownership of FoodPulse's intellectual property to any user.",
      },
      {
        n: 86, title: "Copyright Protection",
        body: "The Platform and its content are protected by applicable copyright laws and international treaties. Unless otherwise indicated, users may not, without prior written permission:",
        bullets: [
          "Copy", "Reproduce", "Republish", "Redistribute", "Sell",
          "License", "Modify", "Translate", "Create derivative works from",
          "Publicly display", "Publicly perform", "Commercially exploit",
        ],
        footer_note: "Limited personal, non-commercial use consistent with these Terms is permitted.",
      },
      {
        n: 87, title: "FoodPulse Trademarks",
        body: "The names, logos, slogans, trade dress, service names, product names, and other brand identifiers associated with FoodPulse are protected by trademark and other intellectual property laws. Examples include:",
        bullets: [
          "FoodPulse®", "FoodPulse logo", "FoodPulse Marketplace",
          "FoodPulse AI", "FoodPulse Insights", "FoodPulse Food Rescue",
          "Any future FoodPulse product or service names",
        ],
        after_bullets: "Users may not:",
        bullets_2: [
          "Impersonate FoodPulse.",
          "Register confusingly similar domain names.",
          "Use FoodPulse branding in advertisements without permission.",
          "Suggest endorsement where none exists.",
          "Alter official logos or trademarks.",
        ],
      },
      {
        n: 88, title: "Third-Party Intellectual Property",
        body: "FoodPulse respects the intellectual property rights of others. Third-party trademarks, product names, business names, logos, and copyrighted materials displayed on the Platform remain the property of their respective owners. Their appearance on FoodPulse does not imply ownership, endorsement, sponsorship, or affiliation unless expressly stated.",
      },
      {
        n: 89, title: "User Content Ownership",
        body: "As described in Chapter 5, users retain ownership of original content they create and submit to FoodPulse. This includes recipes, articles, photographs, videos, illustrations, reviews, comments, educational materials, research contributions, and original graphics.",
        footer_note: "FoodPulse receives only the license necessary to operate, improve, promote, and provide the Platform as described in these Terms.",
      },
      {
        n: 90, title: "Public Data and Facts",
        body: "Facts themselves are generally not protected by copyright. FoodPulse may compile publicly available factual information about food products, agriculture, nutrition, sustainability, and related subjects into original databases and editorial works. While individual facts may not be protected, the selection, organization, presentation, editorial commentary, graphics, databases, and original compilations created by FoodPulse may be protected by copyright, database rights, or other applicable intellectual property laws.",
      },
      {
        n: 91, title: "Limited License to Users",
        body: "Subject to these Terms, FoodPulse grants users a limited, revocable, non-exclusive, non-transferable, non-sublicensable license to access and use the Platform for personal, educational, research, and other lawful purposes. This license does not permit users to:",
        bullets: [
          "Reverse engineer the Platform.",
          "Copy substantial portions of the Platform.",
          "Create competing databases using FoodPulse content.",
          "Remove copyright notices.",
          "Bypass technical protections.",
          "Resell FoodPulse content without authorization.",
        ],
      },
      {
        n: 92, title: "API and Developer Access",
        body: "FoodPulse may provide APIs, software development kits (SDKs), widgets, data feeds, or other developer tools. Use of these services is subject to:",
        bullets: [
          "Separate developer agreements.",
          "Published technical documentation.",
          "Rate limits.", "Security requirements.",
          "Attribution requirements where applicable.",
          "Applicable fees.",
        ],
        footer_note: "FoodPulse may suspend or revoke API access for misuse, security concerns, excessive usage, or violations of these Terms.",
      },
      {
        n: 93, title: "Open Data and Public Contributions",
        body: "FoodPulse may designate certain datasets, educational resources, or community projects as openly available under separate licenses. Where FoodPulse expressly identifies content as subject to an open license, the applicable license terms will govern that content.",
        footer_note: "Unless expressly stated otherwise, no content on FoodPulse should be considered open-source or freely reusable for commercial purposes.",
      },
      {
        n: 94, title: "Copyright Infringement",
        body: "If you believe material available on FoodPulse infringes your copyright, you may submit a copyright notice in accordance with our Copyright and DMCA Policy. A notice should include, where applicable:",
        bullets: [
          "Identification of the copyrighted work.",
          "Identification of the allegedly infringing material.",
          "Information sufficient to locate the material.",
          "Your contact information.",
          "A statement of good-faith belief that the use is unauthorized.",
          "A statement that the information provided is accurate.",
          "A statement that you are authorized to act on behalf of the copyright owner, where applicable.",
          "Any additional information required by applicable law.",
        ],
        footer_note: "FoodPulse may remove or disable access to allegedly infringing material while reviewing a complaint.",
      },
      {
        n: 95, title: "Repeat Infringers",
        body: "FoodPulse reserves the right to suspend or terminate the accounts of users who repeatedly infringe the intellectual property rights of others, consistent with applicable law. In determining whether a user is a repeat infringer, FoodPulse may consider factors such as the number, nature, and outcome of prior complaints.",
      },
      {
        n: 96, title: "Counter-Notices",
        body: "Where permitted by applicable law, users whose content has been removed following a copyright complaint may submit a counter-notice if they believe the removal was made in error or that they have the legal right to use the material.",
        footer_note: "FoodPulse may restore content if appropriate under applicable law and after following the required procedures.",
      },
      {
        n: 97, title: "Feedback",
        body: "Unless otherwise agreed in writing, you grant FoodPulse a perpetual, worldwide, irrevocable, royalty-free, transferable license to use, modify, incorporate, publish, and otherwise exploit any feedback you voluntarily submit without compensation or obligation to you.",
      },
      {
        n: 98, title: "Reservation of Rights",
        body: "Except for the limited licenses expressly granted under these Terms, all rights not expressly granted are reserved by FoodPulse and its licensors. No license or right shall be implied by estoppel, waiver, or otherwise.",
      },
      {
        n: 99, title: "Protection of the FoodPulse Platform",
        body: "To protect the integrity of the Platform, users may not:",
        bullets: [
          "Copy substantial portions of FoodPulse databases.",
          "Use automated bots to extract data without authorization.",
          "Circumvent access controls.", "Mirror the Platform.",
          "Build competing products using unauthorized copies of FoodPulse content.",
          "Misuse APIs or developer tools.",
          "Remove copyright, trademark, or proprietary notices.",
          "Interfere with platform security or technological protection measures.",
        ],
        footer_note: "FoodPulse may employ technical measures to detect and prevent unauthorized access, data scraping, and misuse.",
      },
      {
        n: 100, title: "Requests for Permission",
        body: "Requests to reproduce FoodPulse content, license educational materials, use trademarks, republish articles, use photographs, create commercial partnerships, or access proprietary datasets should be submitted through the contact channels designated by FoodPulse. Permission requests will be evaluated on a case-by-case basis and may be subject to separate licensing agreements.",
      },
    ],
  },
  {
    n: 8,
    title: "Acceptable Use, Fraud Prevention & Platform Integrity",
    blurb: "Prohibited conduct, review integrity, cybersecurity, AI misuse, counterfeit products, and enforcement.",
    sections: [
      {
        n: 101, title: "Our Commitment to a Safe and Trusted Platform",
        body: "FoodPulse is committed to maintaining a trusted, respectful, secure, and transparent platform. Every user shares responsibility for maintaining the integrity of the Platform.",
      },
      {
        n: 102, title: "Acceptable Use",
        body: "Users may use FoodPulse only for lawful purposes and in accordance with these Terms. Permitted activities include:",
        bullets: [
          "Learning about food products", "Sharing educational content",
          "Discovering food products",
          "Buying and selling eligible products", "Offering surplus food",
          "Participating in food rescue initiatives", "Sharing recipes",
          "Reviewing businesses", "Participating in discussions",
          "Promoting sustainable agriculture",
          "Supporting community education", "Conducting lawful research",
          "Using authorized APIs",
        ],
      },
      {
        n: 103, title: "Prohibited Conduct",
        body: "Users may not engage in activities that interfere with the safety, reliability, or integrity of FoodPulse. Prohibited conduct includes, but is not limited to:",
        bullets: [
          "Violating applicable laws or regulations.",
          "Posting false or misleading information.",
          "Creating fake accounts.",
          "Impersonating another person or organization.",
          "Misrepresenting products or businesses.",
          "Manipulating marketplace prices.",
          "Artificially inflating ratings or reviews.",
          "Coordinating fraudulent transactions.",
          "Uploading malicious software.",
          "Circumventing security measures.",
          "Harassing, threatening, or intimidating others.",
          "Publishing defamatory content.",
          "Discriminating against users based on protected characteristics.",
          "Promoting violence or illegal activity.",
          "Infringing intellectual property rights.",
          "Collecting personal information without authorization.",
          "Interfering with Platform operations.",
        ],
      },
      {
        n: 104, title: "Marketplace Fraud",
        body: "FoodPulse prohibits all forms of marketplace fraud. Examples include:",
        bullets: [
          "Fake product listings",
          "Selling products that do not exist",
          "Misrepresenting product quality", "Counterfeit food products",
          "Mislabeling food origin", "False organic certifications",
          "False sustainability claims", "Fraudulent discounts",
          "Non-delivery after payment", "Payment fraud",
          "Identity fraud", "Chargeback fraud",
          "Duplicate listings intended to deceive buyers",
        ],
        footer_note: "FoodPulse may immediately suspend listings or accounts reasonably believed to be involved in fraudulent activity.",
      },
      {
        n: 105, title: "Review Integrity",
        body: "Reviews and ratings are valuable community resources. Users may not:",
        bullets: [
          "Purchase reviews.", "Sell reviews.",
          "Offer incentives for positive reviews without proper disclosure.",
          "Submit reviews for products they have not experienced.",
          "Create fake review accounts.",
          "Coordinate review campaigns intended to manipulate ratings.",
          "Post defamatory or knowingly false reviews.",
        ],
      },
      {
        n: 106, title: "Spam and Unsolicited Communications",
        body: "Users may not use FoodPulse to distribute:",
        bullets: [
          "Spam", "Chain letters", "Pyramid schemes",
          "Unauthorized advertisements", "Mass unsolicited messages",
          "Malware", "Phishing attempts", "Fraudulent promotions",
        ],
      },
      {
        n: 107, title: "Cybersecurity",
        body: "Users may not:",
        bullets: [
          "Attempt unauthorized access.", "Probe or scan Platform security.",
          "Exploit vulnerabilities.", "Introduce viruses or malware.",
          "Conduct denial-of-service attacks.",
          "Interfere with system availability.",
          "Circumvent authentication systems.",
          "Reverse engineer security mechanisms.",
          "Access data belonging to other users without authorization.",
        ],
      },
      {
        n: 108, title: "Automated Access",
        body: "Except through authorized APIs or with prior written permission, users may not:",
        bullets: [
          "Scrape Platform content.", "Use bots to collect data.",
          "Crawl the Platform excessively.", "Build competing databases.",
          "Circumvent rate limits.", "Harvest email addresses.",
          "Automatically create user accounts.",
          "Generate excessive automated traffic.",
        ],
      },
      {
        n: 109, title: "Misuse of Artificial Intelligence",
        body: "Users may not use FoodPulse AI services to:",
        bullets: [
          "Generate fraudulent information.",
          "Create deceptive marketplace listings.",
          "Produce harmful or illegal content.",
          "Impersonate individuals.", "Spread misinformation.",
          "Circumvent Platform safeguards.",
          "Automate abusive behavior.", "Mislead consumers.",
        ],
      },
      {
        n: 110, title: "Food Safety Misrepresentation",
        body: "Users must not knowingly publish false information relating to:",
        bullets: [
          "Food recalls", "Food contamination", "Allergens", "Ingredients",
          "Expiration dates", "Nutrition labels", "Organic status",
          "Country of origin", "Food certifications",
        ],
      },
      {
        n: 111, title: "Counterfeit and Illegal Products",
        body: "Users may not list or promote products that are counterfeit, adulterated, misbranded, stolen, illegal to possess or sell, subject to government prohibition, or produced in violation of applicable laws.",
      },
      {
        n: 112, title: "Protection of Minors",
        body: "Users may not use FoodPulse to exploit minors, collect information from minors unlawfully, publish harmful content directed at minors, or encourage unsafe activities involving minors. FoodPulse may report unlawful activity to appropriate authorities.",
      },
      {
        n: 113, title: "Platform Integrity",
        body: "FoodPulse may employ automated systems and human review to detect fraud, spam, fake reviews, suspicious transactions, account abuse, marketplace manipulation, security threats, and policy violations. Detection methods are proprietary and may change without notice.",
      },
      {
        n: 114, title: "Investigations",
        body: "FoodPulse may investigate suspected violations of these Terms. Investigations may include:",
        bullets: [
          "Reviewing account activity",
          "Reviewing communications made through the Platform",
          "Examining marketplace listings", "Verifying identities",
          "Requesting additional documentation",
          "Temporarily restricting access",
          "Consulting external experts",
          "Cooperating with payment providers",
          "Cooperating with regulatory authorities where legally required",
        ],
      },
      {
        n: 115, title: "Reporting Violations",
        body: "Users are encouraged to report suspected violations involving fraud, spam, counterfeit products, unsafe food, copyright infringement, harassment, fake reviews, marketplace abuse, security vulnerabilities, or illegal activity.",
      },
      {
        n: 116, title: "Enforcement Actions",
        body: "If FoodPulse reasonably believes a user has violated these Terms or applicable law, we may take one or more of the following actions:",
        bullets: [
          "Issue a warning", "Remove content",
          "Correct inaccurate information", "Suspend listings",
          "Restrict marketplace access", "Disable AI features",
          "Freeze user privileges", "Suspend or terminate accounts",
          "Cancel transactions where permitted",
          "Remove business verification",
          "Report unlawful conduct to appropriate authorities",
          "Pursue available legal remedies",
        ],
      },
      {
        n: 117, title: "No Circumvention",
        body: "Users whose accounts or privileges have been restricted or terminated may not create replacement accounts, use another person's account, create business accounts to evade restrictions, use automated systems to regain access, or otherwise circumvent enforcement measures.",
      },
      {
        n: 118, title: "Reservation of Rights",
        body: "FoodPulse reserves the right to take any lawful action reasonably necessary to protect users, businesses, community contributors, intellectual property, marketplace integrity, Platform security, public health, food safety, and the reputation and continued operation of the Platform.",
        footer_note: "Failure to enforce any provision of these Terms in one instance does not constitute a waiver of our right to enforce that or any other provision in the future.",
      },
    ],
  },
  {
    n: 9,
    title: "Account Suspension, Termination, Appeals & Enforcement",
    blurb: "Procedures for suspensions, terminations, appeals, and cooperation with authorities.",
    sections: [
      {
        n: 119, title: "Our Right to Enforce These Terms",
        body: "To protect the safety, integrity, reliability, and reputation of the Platform, FoodPulse reserves the right to investigate violations of these Terms and take appropriate enforcement action. Enforcement decisions may be based on user reports, automated detection systems, internal reviews, information provided by businesses, requests from government authorities, court orders, or other credible sources.",
      },
      {
        n: 120, title: "Suspension of Accounts",
        body: "FoodPulse may temporarily suspend an account or restrict access to some or all Platform features if we reasonably believe that:",
        bullets: [
          "These Terms have been violated.",
          "The account has been compromised.",
          "Fraudulent activity has occurred or is suspected.",
          "False or misleading information has been submitted.",
          "Marketplace abuse has occurred.",
          "Payment disputes require investigation.",
          "The account presents a security risk.",
          "The account threatens public health or food safety.",
          "Temporary suspension is necessary to protect users or the Platform.",
        ],
      },
      {
        n: 121, title: "Immediate Suspension",
        body: "FoodPulse may suspend an account immediately, without prior notice where permitted by law, if we reasonably believe that immediate action is necessary to:",
        bullets: [
          "Protect users from harm.", "Address cybersecurity threats.",
          "Prevent fraud.",
          "Remove dangerous or unlawful content.",
          "Respond to a food safety emergency or product recall.",
          "Comply with a legal obligation.", "Preserve evidence.",
          "Protect the security or stability of the Platform.",
        ],
      },
      {
        n: 122, title: "Termination of Accounts",
        body: "FoodPulse may terminate an account if:",
        bullets: [
          "The user repeatedly violates these Terms.",
          "Fraud or deception has been confirmed.",
          "The account is used for unlawful purposes.",
          "Required verification cannot be completed.",
          "The user intentionally harms other users or the Platform.",
          "A court or government authority requires termination.",
          "The user materially breaches these Terms and fails to remedy the breach where applicable.",
        ],
        footer_note: "Termination may result in the permanent loss of access to some or all FoodPulse services.",
      },
      {
        n: 123, title: "Voluntary Account Closure",
        body: "Users may close their FoodPulse accounts at any time. Closing an account does not automatically cancel completed transactions, eliminate payment obligations, remove public reviews that accurately reflect prior experiences, or affect rights or obligations that arose before account closure. Certain information may be retained as described in these Terms and our Privacy Policy.",
      },
      {
        n: 124, title: "Effect of Suspension or Termination",
        body: "Following suspension or termination, FoodPulse may:",
        bullets: [
          "Disable account access.", "Remove marketplace listings.",
          "Cancel pending listings.", "Restrict messaging features.",
          "Disable AI services.", "Remove business verification.",
          "Prevent new account registration where appropriate.",
          "Preserve records for legal, security, or operational purposes.",
        ],
      },
      {
        n: 125, title: "Pending Transactions",
        body: "If an account is suspended or terminated while transactions are pending, FoodPulse may, where appropriate, allow existing transactions to be completed, cancel transactions that cannot reasonably be fulfilled, restrict new transactions, or hold marketplace privileges pending investigation.",
      },
      {
        n: 126, title: "Appeals",
        body: "Where appropriate, users may request a review of certain enforcement actions by submitting an appeal through the procedures provided by FoodPulse. An appeal should include:",
        bullets: [
          "The account information.",
          "A description of the enforcement action.",
          "The reasons the user believes the action was incorrect.",
          "Supporting documentation, where applicable.",
        ],
        footer_note: "Submitting an appeal does not guarantee reinstatement.",
      },
      {
        n: 127, title: "Review of Appeals",
        body: "Appeals will be reviewed using reasonable procedures that may include human review, automated systems, or a combination of both. FoodPulse may uphold the original decision, modify the enforcement action, restore access, request additional information, or take other appropriate action.",
      },
      {
        n: 128, title: "Repeat Violations",
        body: "FoodPulse may consider a user's history of violations when determining appropriate enforcement actions. Factors considered may include number of prior violations, severity, time between violations, cooperation during investigations, evidence of corrective actions, and impact on users or the Platform.",
      },
      {
        n: 129, title: "Business Account Enforcement",
        body: "Commercial participants may be subject to additional enforcement measures, including removal of business verification, suspension of marketplace privileges, removal of promotional content, restrictions on advertising, temporary withholding of certain business features, and increased verification requirements.",
      },
      {
        n: 130, title: "Preservation of Evidence",
        body: "FoodPulse may preserve records relating to investigations, enforcement actions, or legal obligations, including account information, marketplace activity, user communications made through the Platform, uploaded content, transaction records, security logs, and technical information. Retention periods are governed by applicable law, our Privacy Policy, and legitimate business needs.",
      },
      {
        n: 131, title: "Cooperation with Authorities",
        body: "Where required or permitted by applicable law, FoodPulse may cooperate with law enforcement agencies, regulatory authorities, courts, government agencies, consumer protection organizations, and food safety authorities.",
      },
      {
        n: 132, title: "Survival of Certain Provisions",
        body: "Termination or suspension of an account does not affect provisions of these Terms that by their nature are intended to survive, including provisions relating to intellectual property, user content licenses, payment obligations, warranty disclaimers, limitation of liability, indemnification, dispute resolution, governing law, record retention, and confidentiality obligations, where applicable.",
      },
      {
        n: 133, title: "Reservation of Enforcement Rights",
        body: "FoodPulse's decision to delay or decline enforcement in one instance does not waive its right to enforce these Terms in future situations. FoodPulse may modify its enforcement practices over time to improve fairness, consistency, security, and compliance with evolving legal and regulatory requirements.",
      },
    ],
  },
  {
    n: 10,
    title: "Disclaimers, Warranty Limitations & Limitation of Liability",
    blurb: "The 'AS IS' service, marketplace and food-safety disclaimers, AI limits, force majeure, and liability caps.",
    sections: [
      {
        n: 134, title: "No Professional Advice",
        body: "FoodPulse is an educational, informational, community, and technology platform. The Platform does not provide:",
        bullets: [
          "Medical advice", "Nutritional counseling", "Dietary treatment",
          "Legal advice", "Financial advice", "Tax advice",
          "Agricultural consulting", "Veterinary advice",
          "Professional engineering", "Professional food safety inspections",
        ],
        footer_note: "Users should consult appropriately qualified professionals before making medical, nutritional, agricultural, legal, financial, commercial, or other important decisions.",
      },
      {
        n: 135, title: 'Platform Provided "As Is"',
        body: 'To the fullest extent permitted by applicable law, FoodPulse provides the Platform and all related services on an "AS IS" and "AS AVAILABLE" basis. FoodPulse makes no representation or warranty that:',
        bullets: [
          "The Platform will always be available.",
          "Services will be uninterrupted.",
          "Services will be error-free.",
          "Content will always be accurate.",
          "Bugs will always be corrected.",
          "Services will be secure against every threat.",
          "The Platform will meet every user's expectations.",
        ],
        footer_note: "Use of the Platform is at your own risk.",
      },
      {
        n: 136, title: "No Warranty of Accuracy",
        body: "FoodPulse aggregates information from many sources including businesses, community contributors, government agencies, public databases, scientific publications, artificial intelligence, and third-party providers. Although FoodPulse works to improve the quality of information available through the Platform, we do not warrant that any information is:",
        bullets: [
          "Accurate", "Complete", "Current", "Reliable",
          "Suitable for a particular purpose",
          "Free from omissions or errors",
        ],
      },
      {
        n: 137, title: "Marketplace Disclaimer",
        body: "FoodPulse provides technology that enables buyers and sellers to connect. Unless expressly stated otherwise, FoodPulse is not the seller, buyer, producer, distributor, warehouse operator, transporter, importer, exporter, restaurant, grocery store, farm, or manufacturer.",
        after_bullets: "FoodPulse does not guarantee:",
        bullets_2: [
          "Product availability", "Product quality", "Freshness",
          "Authenticity", "Delivery", "Pricing", "Seller performance",
          "Buyer performance",
        ],
        footer_note: "Transactions occur directly between participating users and businesses.",
      },
      {
        n: 138, title: "Food Safety Disclaimer",
        body: "FoodPulse does not inspect every food product listed on the Platform. FoodPulse does not guarantee that any food:",
        bullets: [
          "Is safe to consume.", "Is free from contamination.",
          "Is free from allergens.", "Meets applicable regulations.",
          "Is fit for a particular dietary requirement.",
        ],
        footer_note: "Food safety remains the responsibility of producers, sellers, restaurants, distributors, and consumers.",
      },
      {
        n: 139, title: "AI Disclaimer",
        body: "AI features may assist users by generating recipes, recommendations, summaries, food identification, nutrition information, and educational content. AI-generated information may be inaccurate, incomplete, outdated, or inappropriate for a particular user or situation.",
        footer_note: "FoodPulse does not warrant the correctness or suitability of AI-generated outputs. Users remain responsible for exercising independent judgment.",
      },
      {
        n: 140, title: "Third-Party Services",
        body: "FoodPulse may integrate or link to third-party services including payment providers, mapping services, delivery providers, government databases, scientific resources, videos, social media platforms, and external websites. FoodPulse does not control third-party services and is not responsible for their availability, security, privacy practices, accuracy, products, services, or content.",
      },
      {
        n: 141, title: "Availability of the Platform",
        body: "FoodPulse may experience interruptions due to scheduled maintenance, software updates, hardware failures, network outages, cybersecurity incidents, force majeure events, or third-party service disruptions. FoodPulse does not guarantee uninterrupted access to the Platform.",
      },
      {
        n: 142, title: "No Guarantee of Marketplace Results",
        body: "FoodPulse does not guarantee that users will sell products, purchase products, find buyers, find sellers, save money, reduce food waste, increase business revenue, receive donations, or achieve any specific commercial or personal outcome.",
      },
      {
        n: 143, title: "No Warranty of Business Relationships",
        body: "FoodPulse does not endorse or guarantee any user, business, organization, or commercial participant unless expressly stated. Business verification, badges, ratings, reviews, or featured listings should not be interpreted as guarantees of product quality, business reliability, financial stability, regulatory compliance, or future performance.",
      },
      {
        n: 144, title: "Limitation of Liability",
        body: "To the fullest extent permitted by applicable law, FoodPulse and its affiliates, officers, directors, employees, contractors, licensors, and service providers shall not be liable for any indirect, incidental, consequential, special, exemplary, or punitive damages arising out of or relating to your use of the Platform. This includes, without limitation:",
        bullets: [
          "Lost profits", "Lost revenue", "Lost business opportunities",
          "Lost data", "Business interruption", "Loss of goodwill",
          "Loss of anticipated savings", "Personal inconvenience",
          "Reputational harm",
          "Emotional distress, where not otherwise recoverable by law",
        ],
        footer_note: "This limitation applies whether the claim is based in contract, tort (including negligence), statute, or any other legal theory, even if FoodPulse has been advised of the possibility of such damages.",
      },
      {
        n: 145, title: "Maximum Liability",
        body: "To the fullest extent permitted by applicable law, FoodPulse's total aggregate liability arising out of or relating to your use of the Platform shall not exceed the greater of:",
        bullets: [
          "The total amount you paid directly to FoodPulse for the specific service giving rise to the claim during the twelve (12) months preceding the event giving rise to the claim; or",
          "One Hundred United States Dollars (US $100).",
        ],
        footer_note: "This limitation does not apply where prohibited by applicable law or to liability that cannot legally be limited or excluded.",
      },
      {
        n: 146, title: "User Responsibility",
        body: "You acknowledge that you are solely responsible for your purchasing decisions, dietary choices, food preparation methods, food storage practices, business decisions, agricultural decisions, marketplace transactions, interactions with other users, and compliance with applicable laws.",
      },
      {
        n: 147, title: "Force Majeure",
        body: "FoodPulse shall not be liable for delays or failures in performance caused by events beyond its reasonable control, including natural disasters, floods, earthquakes, hurricanes, wildfires, pandemics, epidemics, labor disputes, government actions, utility failures, internet outages, cyberattacks, war, terrorism, civil unrest, and transportation disruptions.",
      },
      {
        n: 148, title: "No Waiver of Consumer Rights",
        body: "Nothing in these Terms is intended to exclude, restrict, or limit any rights or remedies that cannot lawfully be excluded or limited under applicable consumer protection laws. Where applicable law provides mandatory rights or guarantees, those rights remain unaffected.",
      },
      {
        n: 149, title: "Severability of Limitations",
        body: "If any portion of this Chapter is determined by a court or competent authority to be invalid or unenforceable, the remaining provisions shall remain in full force and effect to the fullest extent permitted by law.",
      },
    ],
  },
  {
    n: 11,
    title: "Payments, Fees, Billing, Taxes & Financial Services",
    blurb: "Paid services, payment methods, subscriptions, refunds, chargebacks, promotional credits, and taxes.",
    sections: [
      {
        n: 150, title: "Paid Services",
        body: "While many FoodPulse services are available free of charge, certain features may require payment. Paid services may include, but are not limited to:",
        bullets: [
          "Premium memberships", "Business subscriptions",
          "Marketplace transaction services", "Featured business listings",
          "Sponsored product listings", "Advertising services",
          "Promotional campaigns", "API access", "Enterprise services",
          "Data services", "Analytics tools",
          "AI-powered premium features", "Educational programs",
          "Future subscription offerings",
        ],
        footer_note: "Applicable pricing will be disclosed before purchase.",
      },
      {
        n: 151, title: "Payment Methods",
        body: "FoodPulse may accept various payment methods, including credit cards, debit cards, digital wallets, bank transfers, mobile payment services, business invoicing, gift cards (if introduced), promotional credits, and other payment methods supported by our payment providers.",
      },
      {
        n: 152, title: "Third-Party Payment Processors",
        body: "Payments made through FoodPulse may be processed by independent third-party payment providers. FoodPulse does not control the operation or availability of third-party payment systems and is not responsible for errors, outages, delays, or security incidents affecting third-party payment providers.",
      },
      {
        n: 153, title: "Authorization to Charge",
        body: "By purchasing a paid service or completing a transaction, you authorize FoodPulse or its authorized payment processor to charge the selected payment method for purchase amounts, applicable taxes, service fees, subscription fees, delivery charges where applicable, processing fees if disclosed, and other agreed charges.",
      },
      {
        n: 154, title: "Subscription Services",
        body: "Certain FoodPulse services may be offered on a recurring subscription basis. Subscription plans may include monthly plans, annual plans, business plans, enterprise plans, premium marketplace services, AI subscription services, and advertising packages.",
      },
      {
        n: 155, title: "Automatic Renewal",
        body: "Unless otherwise stated, subscriptions may automatically renew at the end of each billing period until canceled. Users may cancel automatic renewal through their account settings or other available cancellation methods.",
      },
      {
        n: 156, title: "Pricing",
        body: "FoodPulse reserves the right to establish, modify, or discontinue pricing for paid services. Prices may vary based on country, currency, subscription level, business category, promotional offers, volume discounts, enterprise agreements, and marketplace services.",
      },
      {
        n: 157, title: "Marketplace Service Fees",
        body: "FoodPulse may charge fees in connection with marketplace services, including transaction commissions, listing fees, featured listings, advertising services, delivery coordination services, payment processing services, business verification services, and premium analytics.",
      },
      {
        n: 158, title: "Advertising and Promotional Services",
        body: "Businesses may purchase promotional services such as sponsored listings, featured products, banner advertising, search placement enhancements, promotional campaigns, event promotions, and seasonal marketing packages.",
        footer_note: "Participation in advertising does not guarantee sales, visibility, customer engagement, or business outcomes.",
      },
      {
        n: 159, title: "Taxes",
        body: "Unless otherwise stated, prices displayed on FoodPulse may exclude applicable taxes. Users are responsible for sales taxes, value-added taxes (VAT), Goods and Services Tax (GST), use taxes, customs duties, import or export charges, and other applicable governmental fees.",
      },
      {
        n: 160, title: "Refund Policy",
        body: "Refund eligibility depends on the nature of the purchased service, applicable law, subscription terms, marketplace transaction terms, and seller policies where applicable. Digital services that have been fully delivered or used may not be eligible for refunds unless required by law.",
      },
      {
        n: 161, title: "Chargebacks and Payment Disputes",
        body: "If a payment is disputed, reversed, or subject to a chargeback, FoodPulse may suspend affected services, restrict marketplace activity, recover outstanding amounts, investigate suspected fraud, request supporting documentation, and charge reasonable administrative fees where permitted by law and disclosed in advance.",
      },
      {
        n: 162, title: "Failed Payments",
        body: "If a payment cannot be completed, FoodPulse may retry the payment, suspend access to paid services, cancel pending purchases, restrict premium features, or require an updated payment method.",
      },
      {
        n: 163, title: "Promotional Credits and Coupons",
        body: "FoodPulse may issue promotional credits, discount codes, coupons, referral rewards, loyalty incentives, and marketplace credits. Unless otherwise stated, promotional credits have no cash value, may be subject to expiration dates, may be limited to specific services, and may not be transferred or resold.",
      },
      {
        n: 164, title: "Currency",
        body: "Prices may be displayed in different currencies depending on your location or account settings. FoodPulse is not responsible for exchange rate fluctuations or fees charged by banks or payment providers.",
      },
      {
        n: 165, title: "Invoices and Receipts",
        body: "FoodPulse may provide electronic receipts, invoices, payment confirmations, subscription records, and tax documentation where applicable. Users are responsible for maintaining their own financial records unless otherwise required by law.",
      },
      {
        n: 166, title: "Business Billing",
        body: "Business customers may qualify for additional billing options, including corporate accounts, consolidated billing, enterprise invoicing, purchase orders, and annual contracts. Business billing arrangements may be governed by separate written agreements in addition to these Terms.",
      },
      {
        n: 167, title: "Financial Compliance",
        body: "FoodPulse reserves the right to refuse, suspend, or cancel transactions where reasonably necessary to comply with applicable laws, prevent fraud, respond to sanctions or legal restrictions, verify user identity, protect the Platform, or protect users and businesses.",
      },
      {
        n: 168, title: "Changes to Paid Services",
        body: "FoodPulse may introduce, modify, suspend, or discontinue paid services, subscription plans, pricing structures, or billing models from time to time. Where required by applicable law, FoodPulse will provide reasonable notice of material changes before they take effect.",
      },
    ],
  },
  {
    n: 12,
    title: "Privacy, Data Protection & Electronic Records",
    blurb: "Data collection, consent, cookies, communications, security, international transfers, and privacy rights.",
    sections: [
      {
        n: 169, title: "Privacy Commitment",
        body: "FoodPulse values the privacy and trust of its users. Our collection, use, storage, disclosure, and protection of personal information are governed by our separate Privacy Policy, which forms an integral part of your use of the Platform.",
        footer_note: "If there is a conflict between these Terms and the Privacy Policy regarding the processing of personal information, the Privacy Policy will govern to the extent of that conflict.",
      },
      {
        n: 170, title: "Information We Collect",
        body: "Depending on your use of the Platform, FoodPulse may collect information including:",
        bullets: [
          "Account information", "Business information",
          "Contact information", "Marketplace transactions", "Listings",
          "Recipes", "Reviews", "Comments", "Uploaded photographs",
          "Uploaded videos", "Messages sent through Platform features",
          "Payment-related information (processed through payment providers where applicable)",
          "Device information", "Browser information", "IP address",
          "Location information (where enabled)", "Usage analytics",
          "AI interaction history", "Customer support communications",
        ],
      },
      {
        n: 171, title: "How We Use Information",
        body: "FoodPulse may use collected information to:",
        bullets: [
          "Operate the Platform.", "Provide marketplace services.",
          "Improve search results.", "Personalize user experiences.",
          "Recommend products and content.", "Process transactions.",
          "Verify business accounts.", "Detect fraud.",
          "Improve AI systems.", "Improve accessibility.",
          "Respond to customer support requests.",
          "Communicate important service updates.",
          "Comply with legal obligations.",
          "Conduct research and analytics using aggregated or de-identified information where appropriate.",
        ],
      },
      {
        n: 172, title: "Cookies and Similar Technologies",
        body: "FoodPulse may use cookies, web beacons, local storage, pixels, device identifiers, analytics technologies, session technologies, and similar technologies. Users may manage cookie preferences through browser settings or other tools made available by FoodPulse, although disabling certain technologies may affect Platform functionality.",
      },
      {
        n: 173, title: "Communications from FoodPulse",
        body: "By creating an account or using the Platform, you consent to receive communications relating to your use of FoodPulse, including account notifications, verification messages, security alerts, password reset requests, marketplace notifications, order updates, subscription information, billing notices, product recall notifications, food safety alerts, changes to these Terms, updates to our Privacy Policy, and customer support responses.",
      },
      {
        n: 174, title: "Marketing Communications",
        body: "With your consent where required by applicable law, FoodPulse may send newsletters, product announcements, promotional offers, marketplace updates, educational articles, event invitations, surveys, and business opportunities. Users may unsubscribe from marketing communications at any time using the unsubscribe tools provided.",
      },
      {
        n: 175, title: "Electronic Consent",
        body: "You agree that FoodPulse may provide notices, agreements, disclosures, invoices, receipts, policies, and other communications electronically. Electronic communications satisfy any legal requirement that such communications be in writing, to the extent permitted by applicable law.",
      },
      {
        n: 176, title: "Maintaining Accurate Information",
        body: "Users are responsible for maintaining accurate and current account information, including name, business name, email address, telephone number, mailing address, payment information, and business licenses where applicable.",
      },
      {
        n: 177, title: "International Data Transfers",
        body: "FoodPulse may operate globally. Personal information may be transferred to, processed in, or stored in countries other than your country of residence, subject to applicable law and appropriate safeguards where required.",
      },
      {
        n: 178, title: "Data Security",
        body: "FoodPulse uses commercially reasonable administrative, technical, and organizational measures designed to protect personal information against unauthorized access, disclosure, alteration, or destruction. However, no Internet service, software system, or electronic storage method can be guaranteed to be completely secure.",
      },
      {
        n: 179, title: "Account Security",
        body: "Users agree to create strong passwords, keep login credentials confidential, enable additional security features where available, promptly report unauthorized account access, log out from shared devices, and avoid sharing account credentials.",
      },
      {
        n: 180, title: "Record Retention",
        body: "FoodPulse may retain information for operational purposes, legal compliance, tax obligations, security investigations, fraud prevention, dispute resolution, business continuity, historical recordkeeping, and scientific or statistical research using anonymized or aggregated information where appropriate.",
      },
      {
        n: 181, title: "Government and Legal Requests",
        body: "FoodPulse may disclose information when required or permitted by applicable law, including in response to court orders, search warrants, subpoenas, regulatory requests, law enforcement requests, public health authorities, and food safety investigations.",
      },
      {
        n: 182, title: "User Privacy Rights",
        body: "Depending on applicable law, users may have rights regarding their personal information, including the right to access, correct, delete, restrict certain processing, object to certain processing, request data portability, withdraw consent where processing is based on consent, and lodge complaints with applicable supervisory authorities.",
      },
      {
        n: 183, title: "Children's Privacy",
        body: "FoodPulse is not intended for children below the minimum age required under applicable law to create an account without appropriate consent. Where required by law, parental or guardian consent must be obtained before personal information is collected from minors.",
      },
      {
        n: 184, title: "Changes to Privacy Practices",
        body: "FoodPulse may update its Privacy Policy and related privacy practices to reflect changes in the Platform, new technologies, legal requirements, business operations, or security improvements. Material changes will be communicated as required by applicable law.",
      },
    ],
  },
  {
    n: 13,
    title: "Dispute Resolution, Governing Law & General Provisions",
    blurb: "Informal resolution, arbitration, governing law, jurisdiction, and closing legal provisions.",
    sections: [
      {
        n: 185, title: "Commitment to Resolving Disputes",
        body: "FoodPulse believes that most disputes can be resolved quickly and fairly through open communication. Before initiating formal legal proceedings, users agree to make a good-faith effort to resolve disputes by contacting FoodPulse through the support channels provided on the Platform.",
      },
      {
        n: 186, title: "Informal Resolution",
        body: "If you have a dispute with FoodPulse, you should first provide:",
        bullets: [
          "Your full name", "Account information", "Contact information",
          "A description of the dispute",
          "Supporting documentation where applicable",
          "The resolution you are requesting",
        ],
      },
      {
        n: 187, title: "Arbitration (Where Applicable)",
        body: "To the extent permitted by applicable law and if specified by FoodPulse for your jurisdiction, disputes arising out of or relating to these Terms or your use of the Platform may be resolved through binding arbitration rather than court proceedings. Where arbitration applies:",
        bullets: [
          "Arbitration will be conducted by a recognized arbitration provider selected by FoodPulse or mutually agreed upon by the parties.",
          "The arbitration will generally be conducted in English unless otherwise required by law or agreed by the parties.",
          "The arbitrator may award the same individual remedies available in court, subject to applicable law.",
          "Arbitration proceedings are generally confidential unless disclosure is required by law.",
        ],
        footer_note: "Nothing in this section prevents either party from seeking temporary or emergency injunctive relief from a court of competent jurisdiction where necessary to prevent immediate and irreparable harm. Where arbitration is prohibited or restricted by applicable law, disputes will be resolved through the courts having jurisdiction.",
      },
      {
        n: 188, title: "Class Action Waiver (Where Permitted)",
        body: "To the fullest extent permitted by applicable law, disputes between you and FoodPulse shall be brought only on an individual basis. You agree not to participate as:",
        bullets: [
          "A plaintiff", "A class representative", "A class member",
          "A private attorney general",
          "A participant in any representative proceeding",
        ],
        footer_note: "If this provision is found unenforceable in your jurisdiction, it shall apply only to the maximum extent permitted by law.",
      },
      {
        n: 189, title: "Governing Law",
        body: "These Terms shall be governed by and construed in accordance with the laws of the jurisdiction in which the FoodPulse operating entity responsible for your use of the Platform is established, without regard to conflict of law principles, except where mandatory local law requires otherwise.",
      },
      {
        n: 190, title: "Jurisdiction",
        body: "Where arbitration does not apply or is unavailable, any legal proceedings relating to these Terms shall be brought before the courts having jurisdiction over the applicable FoodPulse operating entity, unless applicable law requires otherwise.",
      },
      {
        n: 191, title: "Compliance with Laws",
        body: "Users are responsible for ensuring that their use of FoodPulse complies with all applicable laws and regulations, including those relating to consumer protection, food safety, agriculture, advertising, privacy, data protection, export controls, import restrictions, taxation, intellectual property, and electronic commerce.",
      },
      {
        n: 192, title: "Export Controls and Sanctions",
        body: "Users agree not to use the Platform in violation of applicable export control laws, trade restrictions, or economic sanctions. FoodPulse may restrict or terminate access where necessary to comply with applicable sanctions, embargoes, or other legal restrictions.",
      },
      {
        n: 193, title: "Assignment",
        body: "Users may not assign or transfer their rights or obligations under these Terms without FoodPulse's prior written consent. FoodPulse may assign or transfer these Terms, in whole or in part, in connection with a merger, an acquisition, a corporate reorganization, a sale of assets, financing arrangements, or changes in corporate structure.",
      },
      {
        n: 194, title: "Entire Agreement",
        body: "These Terms, together with any documents expressly incorporated by reference — including the Privacy Policy, Community Guidelines, Cookie Policy, Copyright Policy, Marketplace Policies, and any additional terms applicable to specific services — constitute the entire agreement between you and FoodPulse regarding your use of the Platform.",
      },
      {
        n: 195, title: "Amendments",
        body: "FoodPulse may update these Terms from time to time to reflect new services, legal requirements, Platform improvements, security enhancements, or business developments. When material changes are made, FoodPulse will provide notice through appropriate means, such as website notices, email notifications, account dashboards, or mobile applications.",
      },
      {
        n: 196, title: "Severability",
        body: "If any provision of these Terms is determined by a court or other competent authority to be invalid, illegal, or unenforceable, the remaining provisions shall remain in full force and effect. Where legally permissible, the invalid provision shall be interpreted or modified to most closely reflect its intended purpose while remaining enforceable.",
      },
      {
        n: 197, title: "Waiver",
        body: "Failure by FoodPulse to enforce any provision of these Terms shall not constitute a waiver of that provision or any other right. Any waiver must be in writing and signed by an authorized representative of FoodPulse to be effective.",
      },
      {
        n: 198, title: "Survival",
        body: "The following provisions, together with any other provisions that by their nature should survive termination, shall remain effective after your use of the Platform ends:",
        bullets: [
          "Intellectual property", "User content licenses",
          "Payment obligations", "Warranty disclaimers",
          "Limitation of liability", "Indemnification",
          "Dispute resolution", "Governing law",
          "Record retention", "Confidentiality obligations",
          "Audit rights, where applicable",
        ],
      },
      {
        n: 199, title: "Language",
        body: "FoodPulse may make these Terms available in multiple languages. In the event of any inconsistency between translations, the English-language version shall control unless applicable law requires otherwise.",
      },
      {
        n: 200, title: "Contact Information",
        body: "Questions regarding these Terms may be directed to FoodPulse through the contact information published on the Platform. Depending on the nature of your inquiry, FoodPulse may provide dedicated contact channels for:",
        bullets: [
          "Customer Support", "Marketplace Support", "Business Accounts",
          "Privacy Requests", "Copyright and Intellectual Property",
          "Legal Notices", "Security Reports", "Accessibility Requests",
          "Media Inquiries", "Partnership Opportunities",
        ],
      },
      {
        n: 201, title: "Effective Date",
        body: "These Terms become effective on the date indicated at the beginning of this Agreement and remain in effect until replaced or updated by FoodPulse. Users should periodically review the Terms to remain informed of any updates.",
      },
      {
        n: 202, title: "Acceptance of These Terms",
        body: "By accessing or using FoodPulse, creating an account, participating in the Marketplace, submitting User Content, purchasing services, or otherwise interacting with the Platform, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.",
        footer_note: "If you do not agree to these Terms, you must discontinue use of the Platform.",
      },
    ],
  },
];
